import {
  useRemoveMemberFromOrganisationMutation,
  useUpdateOrganisationMemberRoleMutation,
} from '@/app/redux/endpoints/organisation';
import { useAppDispatch } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useUser } from '@/app/redux/hooks/user';
import {
  openConfirmationDialogue,
  setConfirmationDialogueCallback,
} from '@/app/redux/slices/modalsSlice';
import { Invitation } from '@/common/types/organisation';
import SearchInput from '@/common/ui/SearchInput';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { isUserAdmin } from '@/common/utils/organisation';
import api from '@/db/apiClient';
import { PlusIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Skeleton } from '@shadcn/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import styled from 'styled-components';
import EmailInviteDialog from '../EmailInviteDialog';
import { InviteRow, OrganisationMemberRow } from './OrganisationMemberRow';

export default function OrganisationMembers() {
  const dispatch = useAppDispatch();

  const { user } = useUser();
  const { organisation, isLoading } = useOrganisation();
  const [removeMemberFromOrganisation] =
    useRemoveMemberFromOrganisationMutation();
  const [changeOrganisationMemberRole] =
    useUpdateOrganisationMemberRoleMutation();

  const organisationName = organisation?.name || 'Unnamed Organisation';
  const organisationId = organisation?.id;
  const members = organisation?.members ?? [];

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<Invitation[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMembers, setFilteredMembers] = useState(members);
  const [filteredInvites, setFilteredInvites] = useState(pendingInvites || []);

  const isCurrentUserAdmin = useMemo(
    () => isUserAdmin(organisation, user?.uid),
    [organisation, user],
  );

  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      if (a.uid === user?.uid) return -1;
      if (b.uid === user?.uid) return 1;
      if (isUserAdmin(organisation, a.uid) && !isUserAdmin(organisation, b.uid))
        return -1;
      if (!isUserAdmin(organisation, a.uid) && isUserAdmin(organisation, b.uid))
        return 1;
      return 0;
    });
  }, [filteredMembers, organisation, user]);

  useEffect(() => {
    fetchPendingInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredMembers(organisation?.members ?? []);
      setFilteredInvites(pendingInvites);
      return;
    }
    const lower = searchTerm.toLowerCase();
    setFilteredMembers(
      (organisation?.members ?? []).filter(
        (m) =>
          m.name?.toLowerCase().includes(lower) ||
          m.email?.toLowerCase().includes(lower),
      ),
    );
    setFilteredInvites(
      (pendingInvites || []).filter((invite) =>
        invite.email?.toLowerCase().includes(lower),
      ),
    );
  }, [organisation, searchTerm, pendingInvites]);

  function fetchPendingInvites() {
    if (!organisationId) return;
    api.asEducator
      .getPendingInvitesForOrganisation(organisationId)
      .then(setPendingInvites);
  }

  async function handleResendInvite(email: string) {
    try {
      if (!user) {
        throw new Error('User not found');
      }
      if (!organisationId) {
        throw new Error('Organisation ID not found');
      }
      await api.asEducator.sendInviteEmail({
        organisationId,
        organisationName: organisationName,
        email,
        inviterEmail: user.email,
        inviterName: user.name,
      });
      fetchPendingInvites();
      toast.success('Invite resent');
      trackMixpanelEvent('Invite resent', {
        organisationId,
        email,
        inviterEmail: user.email,
        inviterName: user.name,
      });
    } catch (error) {
      console.error('Error resending invite:', error);
      toast.error('Failed to resend invite', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  async function handleDeleteInvite(token: string) {
    try {
      const invite = pendingInvites.find((inv) => inv.token === token);
      await api.asEducator.deleteInvite(token);
      setPendingInvites(
        pendingInvites.filter((invite) => invite.token !== token),
      );

      trackMixpanelEvent('Invite Deleted', {
        inviteEmail: invite?.email,
        deletedBy: user?.email,
      });

      toast.success('Invite deleted');
    } catch (error) {
      console.error('Error deleting invite:', error);
      toast.error('Failed to delete invite', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  async function handleRoleChange(memberId: string, role: 'admin' | 'member') {
    if (!organisationId) {
      toast.error('Failed to change role', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    try {
      const member = members.find((m) => m.uid === memberId);
      if (!member) {
        throw new Error('User not found in org members');
      }

      await changeOrganisationMemberRole({
        orgId: organisationId,
        memberId,
        role,
      }).unwrap();

      trackMixpanelEvent('Member Role Changed', {
        memberEmail: member.email,
        oldRole: member.role,
        newRole: role,
        changedBy: user?.email,
      });

      toast.success('Role updated');
    } catch (error) {
      console.error('Error changing role:', error);
      toast.error('Failed to change role', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  function handleConfirmRemoveMember(userId: string | null) {
    setConfirmationDialogueCallback(async () => {
      await handleRemoveMember(userId);
    });
    dispatch(
      openConfirmationDialogue({
        title: 'Remove member',
        description:
          'Are you sure you want to remove this member from the organisation?',
        variant: 'destructive',
      }),
    );
  }

  async function handleRemoveMember(memberId: string | null) {
    if (!organisationId || !memberId) {
      toast.error('Failed to remove member', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    try {
      const member = members?.find((m) => m.uid === memberId);

      await removeMemberFromOrganisation({
        orgId: organisationId,
        memberId,
      }).unwrap();

      trackMixpanelEvent('Member Removed from Organization', {
        memberEmail: member?.email,
        removedBy: user?.email,
      });

      toast.success('Member removed');
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  const handleCloseInviteDialog = () => {
    setInviteModalOpen(false);
    fetchPendingInvites();
  };

  return (
    <OrganisationMembersContainer>
      <OrganisationMembersHeader>
        <OrganisationMembersTitle>
          Organisation Members
        </OrganisationMembersTitle>
        <OrganisationMembersActions>
          <SearchInput
            placeholder="Search members..."
            value={searchTerm}
            onChange={setSearchTerm}
          />
          {isCurrentUserAdmin && (
            <Button
              className="flex items-center gap-2"
              onClick={() => setInviteModalOpen(true)}
            >
              <PlusIcon size={16} /> Invite Members
            </Button>
          )}
        </OrganisationMembersActions>
      </OrganisationMembersHeader>

      <Table className="border-b border-gray-200">
        <TableHeader>
          <TableRow>
            <TableHead className="w-4"></TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell className="w-4">
                  <Skeleton className="h-8 w-8 rounded-full" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-28" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-40" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-6 w-16" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-8 w-8 ml-auto" />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <>
              {/* Active Section */}
              {sortedMembers.length > 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="bg-muted font-medium text-xs text-muted-foreground uppercase tracking-wider"
                  >
                    <span className="pl-4">
                      {sortedMembers.length} Active member
                      {sortedMembers.length === 1 ? '' : 's'}
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {sortedMembers.map((member) => (
                <OrganisationMemberRow
                  key={member.uid}
                  member={member}
                  isAdmin={isUserAdmin(organisation, member.uid)}
                  isCurrentUserAdmin={isCurrentUserAdmin}
                  onRoleChange={handleRoleChange}
                  onRemoveMember={() => handleConfirmRemoveMember(member.uid)}
                />
              ))}
              {/* Pending Section */}
              {filteredInvites.length > 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="bg-muted font-medium text-xs text-muted-foreground uppercase tracking-wider"
                  >
                    <span className="pl-4">
                      {filteredInvites.length} Pending Invite
                      {filteredInvites.length === 1 ? '' : 's'}
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {filteredInvites.map((invite) => (
                <InviteRow
                  key={invite.email}
                  invite={invite}
                  isCurrentUserAdmin={isCurrentUserAdmin}
                  handleResendInvite={handleResendInvite}
                  handleDeleteInvite={handleDeleteInvite}
                />
              ))}
            </>
          )}
        </TableBody>
      </Table>

      <EmailInviteDialog
        organisationId={organisationId!}
        organisationName={organisationName}
        currentMembers={organisation?.members ?? []}
        currentUserEmail={user?.email || ''}
        pendingInvites={pendingInvites}
        open={inviteModalOpen}
        onClose={handleCloseInviteDialog}
      />
    </OrganisationMembersContainer>
  );
}

const OrganisationMembersContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const OrganisationMembersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid hsl(var(--border));
`;

const OrganisationMembersTitle = styled.h1`
  font-size: 20px;
  font-weight: 500;
  color: hsl(var(--foreground));
`;

const OrganisationMembersActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;
